import { isLessThan } from '../shared/mq';

let THREE,
  gsap,
  colors,
  maxZ,
  z = 0;

export default class Experience {
  constructor(root) {
    console.log('🌈 Experience', this);
    // Elements
    this.root = root;
    this.nav = document.querySelectorAll('.u-nav-portal, .u-nav-header');
    this.container = this.root.querySelector('.u-experience__container');
    this.canvas = this.root.querySelector('canvas');
    this.trigger = this.root.querySelector('[data-experience-trigger]');
    this.close = this.root.querySelector('[data-experience-close]');
    this.sources = [...window.experience];
    this.legend = this.root.querySelector('[data-experience-legend]');
    this.tip = this.root.querySelector('.u-experience__container__tip');
    this.legendTitle = this.legend.querySelector('.title');
    this.legendSubtitle = this.legend.querySelector('.subtitle');
    this.legendLink = this.legend.querySelector('a');
    this.legendLinkLabel = this.legendLink.querySelector('.split-lines-inner');

    // Global properties
    this.isActive = false;
    this.scrollCoef = 0.005;
    this.hasOnboarded = sessionStorage.getItem('experience-onboarding-played');

    // Circle properties
    this.angle = 0;
    this.radius = 150;
    this.diameter = Math.PI * 2 * this.sources.length;

    THREE = window.THREE;
    gsap = window.gsap;

    colors = [
      new THREE.Color('rgb(0, 112, 127)'),
      new THREE.Color('rgb(44, 163, 73)'),
      new THREE.Color('rgb(0, 190, 219)'),
    ];
    maxZ = isLessThan('medium')
      ? window.experience.length * 6
      : window.experience.length * 4;

    this.init();
  }

  /*
   * Initialization
   */

  init() {
    this.detectBrowser();
    this.bind();
    this.initEvents();
    this.initScene();

    if (this.hasOnboarded) {
      document.querySelector('.u-museum').classList.add('onboarded');
    }
  }

  detectBrowser() {
    // console.log(Detectizr, Detectizr.browser.name, Detectizr.browser.major);
    if (Detectizr.browser.name === 'firefox' && Detectizr.browser.major < 88) {
      this.scrollCoef = 0.2;
    }

    this.isMobile = Detectizr.device.type === 'mobile';

    if (this.isMobile) {
      this.scrollCoef = 0.1;
    }
  }

  bind() {
    this.activate = this.activate.bind(this);
    this.deactivate = this.deactivate.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.onLegendClick = this.onLegendClick.bind(this);
    this.tick = this.tick.bind(this);
    this.selectObject = this.selectObject.bind(this);
    this.unselect = this.unselect.bind(this);
  }

  initEvents() {
    this.trigger.addEventListener('click', this.activate);
    this.close.addEventListener('click', this.deactivate);
    this.legendLink.addEventListener('click', this.onLegendClick);

    window.addEventListener('resize', this.onResize);
    window.addEventListener('keyup', this.onKeyUp);

    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('click', this.onClick);
    document.addEventListener('wheel', this.onWheel, {
      passive: false,
    });
    document.addEventListener('touchstart', this.onTouchStart, {
      passive: false,
    });
    document.addEventListener('touchmove', this.onTouchMove, {
      passive: false,
    });
  }

  initScene() {
    this.context = this.canvas.getContext('webgl');
    this.scene = new THREE.Scene();

    {
      const color = new THREE.Color('rgb(0, 22, 24)');
      const density = 0.08;
      this.scene.fog = new THREE.FogExp2(color, density);
    }
    this.mouse = new THREE.Vector2();
    this.touch = new THREE.Vector2();
    this.intersected = null;
    this.selected = null;

    this.sizes = {
      width: this.root.clientWidth,
      height: window.innerHeight,
    };

    // Renderer
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
    });
    this.renderer.setSize(
      this.canvas.clientWidth,
      this.canvas.clientHeight + 60
    );
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    // Camera
    this.camera = new THREE.PerspectiveCamera(
      75,
      this.sizes.width / this.sizes.height
    );
    this.cameraZ = this.hasOnboarded ? maxZ + 10 : maxZ + 20;
    this.camera.position.z = this.cameraZ;
    /*
    // place camera in 'circle' mode
    this.camera.position.x = 0;
    this.camera.position.y = this.radius / -5;
    this.camera.position.z = this.radius;
    this.camera.rotation.x = -Math.PI / 4;
    */
    this.scene.add(this.camera);

    // Loader
    this.loader = new THREE.TextureLoader();

    // Raycaster
    this.raycaster = new THREE.Raycaster();

    this.objects = new THREE.Group();
    this.scene.add(this.objects);
    this.sources = this.sources.sort(() => Math.random() - 0.5); // shuffle array
    this.addObjects();

    this.triangles = new THREE.Group();
    this.scene.add(this.triangles);
    this.addTriangles();
    this.tick();
  }

  onboard() {
    const page = document.querySelector('.u-museum');
    const nav = page.querySelector('.u-nav');
    const header = page.querySelector('.u-nav-header');
    const experience = page.querySelector('.u-experience');
    const title = page.querySelector('.u-experience__heading h1');
    const link = page.querySelector('.u-experience__trigger');
    const introduction = page.querySelector('.u-experience__introduction');
    const tl = gsap.timeline({
      onComplete: () => {
        page.classList.add('onboarded');
        sessionStorage.setItem('experience-onboarding-played', '1');
      },
    });

    this.cameraZ = maxZ - 10;
    tl.set([title, introduction], { y: 50 });
    tl.to(page, { opacity: 1, duration: 1.5 });
    tl.add('visible');
    tl.to(experience, { opacity: 1, duration: 1.5 }, 'visible');
    tl.to(
      this.camera.position,
      { z: this.cameraZ, duration: 4, ease: 'Power4.in' },
      'visible'
    );
    tl.to([nav, header], { opacity: 1 }, 'visible+=1');
    tl.to([title, link], { opacity: 1, y: 0, stagger: 0.5 }, 'visible+=2');
    tl.to(introduction, { opacity: 1, y: 0, duration: 1.5 }, '-=0.5');
  }

  /*
   * Change the state of the experience
   */

  activate(e) {
    e.preventDefault();
    e.stopPropagation();

    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    this.isActive = true;
    this.root.classList.add('is-active');
    this.renderer.setSize(this.canvas.clientWidth, this.canvas.clientHeight);

    const tl = gsap.timeline({
      onStart: () => {
        document.querySelector('.u-museum').classList.add('no-scroll');
        document.body.style.overscrollBehavior = 'none';
        document.querySelector('.u-experience__introduction').style.zIndex = 10;

        z = -1;
      },
    });
    tl.set(this.canvas, { position: 'fixed' });
    tl.to(this.container, { height: '100vh', ease: Power4.in });
    tl.to(this.nav, { yPercent: -130, ease: Power4.in }, 0);
  }

  deactivate() {
    this.root.classList.remove('is-active');

    if (this.selected) {
      this.unselect();
    }

    const tl = gsap.timeline({
      onStart: () => {
        this.isActive = false;

        document.querySelector('.u-museum').classList.remove('no-scroll');
        document.body.removeAttribute('style');
      },
      onComplete: () => {
        document
          .querySelector('.u-experience__introduction')
          .removeAttribute('style');
        gsap.set([this.container, this.nav], {
          clearProps: 'all',
        });

        window.cancelAnimationFrame(this.tick);
      },
    });

    tl.set(this.canvas, { position: 'absolute' });
    tl.to(this.camera.position, { z: this.camera.position.z + 0.1 }, 0);
    tl.to(this.camera.rotation, { x: 0, y: 0 }, 0);
    tl.to(this.container, { height: '80vh', ease: Power4.out });
    tl.to(this.nav, { yPercent: 0, ease: Power4.out }, 0);
  }

  /*
   * Create objects etc
   */

  addObjects() {
    this.sources.forEach((source, index) => this.createObject(index));
  }

  addTriangles() {
    for (let i = 0; i < this.sources.length * 3; i++) {
      this.createTriangle(i);
    }
  }

  createObject(index) {
    const maxWidth = 6;
    let object;

    this.loader.load(this.sources[index].src, (texture) => {
      const ratio = maxWidth / texture.image.width;
      const width = maxWidth;
      const height = texture.image.height * ratio;

      const geometry = new THREE.PlaneGeometry(width, height);
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        // transparent: true,
        // opacity: 1,
      });

      object = new THREE.Mesh(geometry, material);

      this.objects.add(object);
      object.userData.initialPosition = { ...object.position };
      this.positionAlongLine(object, index);

      const percentage = (this.sources.length - index) / this.sources.length;

      object.position.z = maxZ * percentage;
      object.userData.initialPosition.z = object.position.z;

      object.updateMatrixWorld();

      // const opacity = 1 - (this.camera.position.z - object.position.z) / 30;
      // object.material.opacity = opacity > 0 ? opacity : 0;

      object.userData.index = index;

      if (
        this.objects.children.length === this.sources.length &&
        !this.hasOnboarded
      ) {
        this.onboard();
      }
    });
  }

  createTriangle(index) {
    const material = new THREE.MeshBasicMaterial({
      color: colors[Math.round(Math.random() * 2)],
      transparent: true,
      reflectivity: 0,
      opacity: 0.5,
    });
    const geometry = new THREE.BufferGeometry();
    const size = Math.round(Math.random() * 3) / 10;
    const vertices = [];
    vertices.push(-size, -size, 0);
    vertices.push(size, -size, 0);
    vertices.push(0, size, 0);

    geometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(vertices, 3)
    );
    geometry.computeVertexNormals();

    const triangle = new THREE.Mesh(geometry, material);
    this.triangles.add(triangle);

    const maxOffset = 4;
    const percentage =
      ((this.sources.length * 3 - index) / this.sources.length) * 5;

    triangle.position.z = (maxZ * percentage) / 5;
    triangle.position.x = Math.random() * maxOffset * 2 - maxOffset;
    triangle.position.y = Math.random() * maxOffset * 2 - maxOffset;

    triangle.userData.initialPosition = { ...triangle.position };

    triangle.rotation.z = Math.random() * Math.PI;

    // const opacity = 1 - (this.camera.position.z - triangle.position.z) / 10;
    // triangle.material.opacity = opacity > 0 ? opacity : 0;

    triangle.userData.index = index;
  }

  positionAlongLine(object, index) {
    const coef = index % 4;
    const maxOffsetX = isLessThan('medium') ? 7 : 11;
    const maxOffsetY = isLessThan('medium') ? 9 : 9;

    /*
    0: -1 0, 0 1
    1: -1 0, -1 0
    2: 0 1, 0 1
    3: 0 1, -1 0
    */

    let x, y;

    switch (index) {
      case 0:
        x = -10;
        y = 5;

        break;
      case 1:
        x = -8;
        y = -2;

        break;
      case 2:
        x = 6;
        y = 5;

        break;
      case 3:
        x = 8;
        y = -3;

        break;
      default:
        x = Math.random() * maxOffsetX + 0.1;
        y = Math.random() * maxOffsetY;

        if (coef === 0 || coef === 1) {
          x *= -1;
        }

        if (coef === 1 || coef === 3) {
          y *= -1;
        }

        break;
    }

    object.position.x = x;
    object.position.y = y;

    // Store positions for selection animation
    object.userData.initialPosition.x = x;
    object.userData.initialPosition.y = y;
    object.userData.selectedPosition = { x: (x *= 1.2), y: (y *= 1.2) };

    object.rotation.x = (-object.position.y / 100) * Math.PI;
    object.rotation.y = (-object.position.x / 90) * Math.PI;
  }

  positionAlongCircle(object, index) {
    const maxOffsetX = 10;
    const angle = (index / this.sources.length) * 360;

    object.userData.angle = angle;

    object.position.x = Math.random() * maxOffsetX - maxOffsetX / 2;
    object.position.y = Math.sin(angle) * this.radius;
    object.position.z = Math.cos(angle) * this.radius;

    // const distance = Math.abs(this.angle - angle);
    // const opacity = 1 - distance / 50;
    // object.material.opacity = opacity > 0 ? opacity : 0;
    // console.log(index, angle, distance, opacity);
  }

  /*
   * Animation loop
   */

  tick() {
    if (this.isActive) {
      // Find intersections
      this.raycaster.setFromCamera(this.mouse, this.camera);

      const intersects = this.raycaster.intersectObjects(this.objects.children);

      if (intersects.length > 0) {
        const furthestZ = isLessThan('medium') ? 20 : 30;
        let target = intersects[0].object;

        if (this.cameraZ - intersects[0].object.position.z < 2) {
          target = intersects[1] ? intersects[1].object : null;
        }

        if (
          target &&
          this.intersected !== target &&
          this.camera.position.z - target.position.z < furthestZ
        ) {
          this.intersected = target;
          this.canvas.style.cursor = 'pointer';

          if (this.isMobile) {
            this.selectObject(this.intersected);
          } else {
            gsap.to(this.intersected.scale, {
              x: 1.1,
              y: 1.1,
              z: 1.1,
              duration: 0.5,
            });
          }
        }
      } else if (this.intersected && !this.selected) {
        gsap.to(this.intersected.scale, {
          x: 1,
          y: 1,
          z: 1,
          duration: 0.5,
          onComplete: () => {
            this.intersected = null;
            this.canvas.style.cursor = 'default';
          },
        });
      } else {
        // this.unselect();
        this.intersected = null;
        this.canvas.style.cursor = 'default';
      }

      this.updateCamera();
      this.updateRotations();
    }

    // Render
    this.renderer.render(this.scene, this.camera);
    window.requestAnimationFrame(this.tick);
  }

  // Old
  updateOpacities() {
    this.objects.children.forEach((object, i) => {
      // if (this.selected) {
      //   if (this.selected.el === object) {
      //     object.material.opacity = 1;
      //   } else {
      //     object.material.opacity = 0.5;
      //   }
      // } else {
      //   object.material.opacity = 0.8;
      // }
      /*
      if (this.selected && this.selected.el === object) {
        return;
      }
      let maxOpacity = this.selected ? 0.1 : 1;
      const opacity = 1 - (this.cameraZ - object.position.z) / 30;

      if (this.selected && object.material.opacity > 0.1) {
        gsap.to(object.material, { opacity: 0.1 });

        return;
      }

      object.material.opacity = opacity <= maxOpacity ? opacity : maxOpacity;

      if (this.cameraZ > maxZ + 20 || this.cameraZ < 5) {
        object.material.opacity = 0;
      }

      if (object.position.z > this.cameraZ - 6) {
        object.material.opacity = 0;
      }
      */
    });

    // this.triangles.children.forEach((triangle) => {
    //   if (this.cameraZ > maxZ + 20 || this.cameraZ < 4) {
    //     triangle.material.opacity = 0;
    //   } else {
    //     const opacity = 1 - (this.camera.position.z - triangle.position.z) / 20;
    //     triangle.material.opacity = opacity > 0 ? opacity : 0;
    //   }
    // });
  }

  updateRotations() {
    if (this.isMobile) {
      return;
    }

    const target = new THREE.Vector2();

    target.x = this.mouse.x * -0.5;
    target.y = this.mouse.y * 0.5;

    this.objects.children.forEach((object) => {
      object.rotation.x += 0.025 * (target.y - object.rotation.x);
      object.rotation.y += 0.025 * (target.x - object.rotation.y);
    });
  }

  // Updates objects in 'circle' mode
  updateObjects() {
    z *= 0.9;
    const deltaZ = parseFloat(z.toFixed(4));

    this.angle += deltaZ;
    this.angle = this.angle % 360;

    this.objects.rotation.x = -this.angle;

    this.objects.children.forEach((object) => {
      // object.rotation.x = (-Math.PI / 2) * this.angle;
    });
  }

  updateCamera() {
    const rotationCoef = this.isMobile ? 0 : 0.05;
    const target = new THREE.Vector2();

    target.x = this.mouse.x * -0.5;
    target.y = this.mouse.y * 0.5;

    const deltaX = target.y - this.camera.rotation.x;
    const deltaY = target.x - this.camera.rotation.y;

    this.camera.rotation.x += rotationCoef * deltaX;
    this.camera.rotation.y += rotationCoef * deltaY;

    if (this.selected && !isLessThan('medium')) {
      // Legend
      const position = this.selected.el.position.clone();
      const boundingBox = new THREE.Box3().setFromObject(this.selected.el);
      // const displayOnRight = this.selected.el.position.x < 1;

      position.x = boundingBox.max.x;
      position.project(this.camera);

      let x = (position.x * 0.5 + 0.5) * this.canvas.clientWidth - deltaX;
      const y = (position.y * -0.5 + 0.5) * this.canvas.clientHeight - deltaY;

      this.legend.style.transform = `translate(${x}px,${y}px)`;
    }

    if (this.cameraAnimating) {
      z = 0;
      this.cameraZ = this.camera.position.z;
      this.camera.updateMatrixWorld();

      return;
    }

    this.cameraZ += z;
    z *= 0.9;

    /*
    // update camera in 'circle' mode
    const deltaZ = parseFloat(z.toFixed(4));

    this.cameraAngle += deltaZ;
    this.cameraAngle = this.cameraAngle % 360;

    this.camera.position.x = 0;
    this.camera.position.y = Math.sin(this.cameraAngle) * this.radius;
    this.camera.position.z = Math.cos(this.cameraAngle) * this.radius;

    this.camera.rotation.x = -this.cameraAngle;
    console.log(this.cameraAngle);
    */

    if (this.cameraZ > maxZ + 20) {
      // console.log('RESET to zero');
      this.cameraZ = 5;
    } else if (this.cameraZ < 4) {
      // console.log('RESET to max');
      this.cameraZ = maxZ + 15;
      this.objects.children.forEach((object, index) => {
        this.positionAlongLine(object, index);
      });
      this.triangles.children.forEach((triangle, index) => {
        this.positionAlongLine(triangle, index);
      });
    }

    this.camera.position.z = this.cameraZ;
    this.camera.updateMatrixWorld();
  }

  /*
   * Interactions
   */

  selectObject(el) {
    if (this.isAnimating) {
      return;
    }

    this.selected = {
      el: el,
      initialPosition: { ...el.position },
      initialRotation: { ...el.rotation },
    };

    this.legendTitle.innerHTML = this.sources[el.userData.index].title;
    this.legendSubtitle.innerHTML = this.sources[el.userData.index].subtitle
      ? this.sources[el.userData.index].subtitle
      : '';

    if (this.sources[el.userData.index].url) {
      this.legendLinkLabel.innerHTML = this.sources[el.userData.index].liburl;
      this.legendLink.setAttribute('href', this.sources[el.userData.index].url);
      this.legendLink.style.display = 'block';
    } else {
      this.legendLink.style.display = 'none';
    }

    const tl = gsap.timeline({
      onStart: () => {
        this.isAnimating = true;
        this.cameraAnimating = true;
      },
      onComplete: () => {
        this.legend.classList.add('is-visible');
        this.isAnimating = false;
        this.cameraAnimating = false;
      },
    });

    this.objects.children.forEach((object) => {
      if (object !== this.selected.el) {
        tl.to(
          object.position,
          {
            x: object.userData.selectedPosition.x,
            y: object.userData.selectedPosition.y,
            duration: 1,
            ease: 'Power4.out',
          },
          0
        );

        if (object.position.z > this.selected.initialPosition.z) {
          tl.to(
            object.position,
            {
              z: object.position.z + 5,
              duration: 1,
              ease: 'Power4.out',
            },
            0
          );
        }
      }
    });

    this.triangles.children.forEach((triangle) => {
      if (this.camera.position.z - triangle.position.z <= 10) {
        tl.to(
          triangle.material,
          {
            opacity: 0,
            duration: 1,
            ease: 'Power4.out',
          },
          0
        );
      }
    });

    tl.to(this.tip, { opacity: 0 }, 0)
      .to(
        this.selected.el.position,
        {
          x: 0,
          y: isLessThan('medium') ? 1.5 : 0,
          z: isLessThan('medium')
            ? this.selected.el.position.z - 3
            : this.selected.el.position.z,
          ease: 'expo.out',
          duration: 1,
        },
        0
      )
      .to(this.selected.el.rotation, { x: 0, y: 0, duration: 1 }, 0)
      // Camera
      .to(
        this.camera.position,
        {
          z: this.selected.el.position.z + 6.5,
        },
        0
      );
  }

  unselect() {
    if (this.isAnimating || !this.selected) {
      return;
    }

    this.legend.classList.remove('is-visible');

    const tl = gsap.timeline({
      onStart: () => {
        this.legend.classList.remove('is-visible');
        this.isAnimating = true;
      },
      onComplete: () => {
        this.selected = null;
        this.isAnimating = false;
      },
    });

    tl.to(this.tip, { opacity: 1 }, 0)
      .to(
        this.selected.el.rotation,
        {
          x: this.selected.initialRotation._x,
          y: this.selected.initialRotation._y,
          duration: 0.5,
        },
        0
      )
      .to(
        this.selected.el.scale,
        {
          x: 1,
          y: 1,
          z: 1,
          duration: 0.5,
        },
        0
      );

    this.objects.children.forEach((object) => {
      tl.to(
        object.position,
        {
          x: object.userData.initialPosition.x,
          y: object.userData.initialPosition.y,
          z: object.userData.initialPosition.z,
          duration: 0.5,
          ease: 'Power4.in',
        },
        0
      );
    });

    this.triangles.children.forEach((triangle) => {
      tl.to(
        triangle.material,
        {
          opacity: 0.75,
          duration: 0.5,
        },
        0
      );
    });
  }

  /*
   * Events
   */

  onResize() {
    // this.sizes.width = window.innerWidth;
    this.sizes.width = this.root.clientWidth;
    this.sizes.height = window.innerHeight;

    this.camera.aspect = this.sizes.width / this.sizes.height;
    this.camera.updateProjectionMatrix();

    this.renderer.setSize(this.sizes.width, this.sizes.height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  }

  onMouseMove(e) {
    const coef = 2;
    const rect = this.renderer.domElement.getBoundingClientRect();

    this.mouse.x =
      ((e.clientX - rect.left) / (rect.width - rect.left)) * coef - 1;
    this.mouse.y =
      -((e.clientY - rect.top) / (rect.bottom - rect.top)) * coef + 1;
  }

  onClick() {
    if (!this.isActive) {
      return;
    }

    if (this.intersected) {
      if (this.selected && this.selected.el === this.intersected) {
        this.unselect();
      } else {
        this.selectObject(this.intersected);
      }
    } else if (this.selected) {
      this.unselect();
    }
  }

  onWheel(e) {
    if (!this.isActive) {
      return;
    }

    e.preventDefault();

    if (this.selected) {
      this.unselect();
    }

    z = e.deltaY * -this.scrollCoef;
  }

  onTouchStart(e) {
    if (!this.isActive) {
      return;
    }

    const y = e.targetTouches ? e.targetTouches[0].pageY : e;

    this.touchStartY = y;
  }

  onTouchMove(e) {
    if (!this.isActive) {
      return;
    }

    e.preventDefault();

    if (this.selected) {
      this.unselect();
    }

    const y = e.targetTouches ? e.targetTouches[0].pageY : e;
    const deltaY = (this.touchStartY - y) * 10;

    z = deltaY * 0.001;
  }

  onKeyUp(e) {
    if (!this.isActive) {
      return;
    }

    if (e.key === 'Escape') {
      this.deactivate();
    }

    if (e.key === 'ArrowUp') {
      z = -0.5;
      this.unselect();
    }

    if (e.key === 'ArrowDown') {
      z = 0.5;
      this.unselect();
    }
  }

  onLegendClick(e) {
    const href = e.currentTarget.href;
    e.preventDefault();

    gsap.to(document.body, {
      opacity: 0,
      onComplete: () => {
        window.location = href;
      },
    });
  }
}
